/**
 * Phase 30 — Optional Local Sample Collection admin page.
 *
 * Route: /admin/partners/sample-collection
 *
 * PINNED USER RULE (Feb 2026):
 *   Configuration + visibility only.
 *   • No emails, SMS, social posts, partner notifications, customer
 *     notifications, login invites, password resets or login
 *     credentials.
 *   • No ServiceM8 jobs, work orders, technician dispatch,
 *     scheduling, route plans, calendar tasks, quotes or proposals.
 *   • No commission, core-test pricing, white-label, partner status,
 *     preferred status, public allocation or historical attribution
 *     changes.
 *   • Audit log is append-only.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, Loader2, AlertTriangle, Save, History,
  MapPin, Mail, Phone, ListChecks,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";


const PRICE_MODE_LABEL = {
  not_offered:            "Not offered",
  included:               "Included in booking",
  quoted_separately:      "Quoted separately",
  admin_configured_price: "Admin-configured price",
};


const CONTACT_METHOD_LABEL = {
  not_specified:  "Not specified",
  partner_direct: "Partner direct",
  scwt_admin:     "Aware admin",
  booking_form:   "Booking form",
};


function formatAU(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-AU", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}


export default function PWSSampleCollection() {
  const [partners, setPartners] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await api.get("/admin/pws-sample-collection/list");
      setPartners(r.data.rows || []);
      if (!selectedId && (r.data.rows || []).length > 0) {
        setSelectedId(r.data.rows[0].id);
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail)
                  || "Failed to load partners");
    } finally {
      setLoadingList(false);
    }
  }, [selectedId]);

  useEffect(() => { loadList(); }, [loadList]);

  const loadDetail = useCallback(async (pid) => {
    if (!pid) {
      setDetail(null); setDraft(null); setAudit([]);
      return;
    }
    setLoadingDetail(true);
    try {
      const [d, a] = await Promise.all([
        api.get(`/admin/pws-sample-collection/${pid}`),
        api.get(`/admin/pws-sample-collection/${pid}/audit`),
      ]);
      setDetail(d.data);
      setDraft({
        enabled:         !!d.data.settings.local_sample_collection_enabled,
        note:            d.data.settings.local_sample_collection_note || "",
        service_area:    d.data.settings.local_sample_collection_service_area || "",
        postcodes:       (d.data.settings.local_sample_collection_postcodes || []).join(", "),
        price_mode:      d.data.settings.local_sample_collection_price_mode || "not_offered",
        price:           d.data.settings.local_sample_collection_price ?? "",
        contact_method:  d.data.settings.local_sample_collection_contact_method || "not_specified",
      });
      setAudit(a.data.rows || []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail)
                  || "Failed to load partner settings");
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

  const dirty = useMemo(() => {
    if (!detail || !draft) return false;
    const s = detail.settings;
    return (
      draft.enabled !== !!s.local_sample_collection_enabled ||
      draft.note !== (s.local_sample_collection_note || "") ||
      draft.service_area !== (s.local_sample_collection_service_area || "") ||
      draft.postcodes !== (s.local_sample_collection_postcodes || []).join(", ") ||
      draft.price_mode !== (s.local_sample_collection_price_mode || "not_offered") ||
      String(draft.price ?? "") !== String(s.local_sample_collection_price ?? "") ||
      draft.contact_method !== (s.local_sample_collection_contact_method || "not_specified")
    );
  }, [detail, draft]);

  const save = async () => {
    if (!detail || !draft) return;
    setSaving(true);
    const postcodes = draft.postcodes
      .split(",").map((p) => p.trim()).filter(Boolean);
    const body = {
      local_sample_collection_enabled:        draft.enabled,
      local_sample_collection_note:           draft.note,
      local_sample_collection_service_area:   draft.service_area,
      local_sample_collection_postcodes:      postcodes,
      local_sample_collection_price_mode:     draft.price_mode,
      local_sample_collection_contact_method: draft.contact_method,
    };
    if (draft.price_mode === "admin_configured_price") {
      body.local_sample_collection_price = Number(draft.price) || 0;
    } else {
      body.local_sample_collection_price = null;
    }
    try {
      await api.put(`/admin/pws-sample-collection/${detail.id}`, body);
      toast.success("Settings saved");
      await loadDetail(detail.id);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail)
                  || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loadingList && partners.length === 0) {
    return (
      <PortalShell>
        <div className="min-h-[40vh] flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-cyan-600 animate-spin" />
        </div>
      </PortalShell>
    );
  }

  return (
    <PortalShell>
      <div
        data-testid="ph30-sample-collection-page"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-7"
      >
        <div>
          <Link
            to="/admin/partners/network"
            className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
          >
            <ArrowLeft className="w-3 h-3" /> Partners
          </Link>
          <h1
            data-testid="ph30-page-title"
            className="font-display mt-2 text-3xl sm:text-4xl font-bold text-slate-900"
          >
            Optional Local Sample Collection
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-3xl">
            Configure which active PWS partners can support optional
            local sample collection and how it should be displayed at
            the booking page. This page does not dispatch jobs,
            create schedules or send notifications.
          </p>
        </div>

        <div
          data-testid="ph30-safety-banner"
          className="flex items-start gap-3 rounded-2xl border border-emerald-300 bg-emerald-50 px-5 py-4 text-sm text-emerald-900"
        >
          <ShieldCheck className="w-5 h-5 mt-0.5 text-emerald-600 flex-shrink-0" />
          <div>
            <div className="font-semibold">Configuration only.</div>
            <div className="mt-0.5">
              No collection is scheduled, no technician is dispatched,
              no ServiceM8 job is created, no email or SMS is sent,
              and no commission/pricing/partner-status/white-label
              changes occur. Every change is recorded in the audit
              log.
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 p-5">
          <label className="text-[11px] font-bold uppercase text-slate-500">
            Active partner
          </label>
          <select
            data-testid="ph30-partner-select"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="mt-1 block rounded-md border-slate-300 text-sm min-w-[320px]"
          >
            {partners.map((p) => (
              <option key={p.id} value={p.id}>
                {`${p.business_name} (${p.partner_slug})`}
              </option>
            ))}
          </select>
          {partners.length === 0 ? (
            <div
              data-testid="ph30-no-eligible-partners"
              className="mt-2 text-xs text-amber-700"
            >
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              No active PWS partners found.
            </div>
          ) : null}
        </div>

        {loadingDetail ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 text-cyan-600 animate-spin" />
          </div>
        ) : detail && draft ? (
          <EditorPanel
            detail={detail}
            draft={draft}
            setDraft={setDraft}
            dirty={dirty}
            saving={saving}
            onSave={save}
          />
        ) : null}

        {audit && audit.length > 0 ? (
          <AuditPanel rows={audit} />
        ) : null}
      </div>
    </PortalShell>
  );
}


function EditorPanel({ detail, draft, setDraft, dirty, saving, onSave }) {
  const update = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  return (
    <div
      data-testid="ph30-editor-panel"
      className="rounded-2xl bg-white border border-slate-200 p-6 space-y-6"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display text-lg font-bold text-slate-900">
            {detail.business_name}
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {detail.partner_slug} ·{" "}
            {detail.franchise_region || "—"} · {detail.state || "—"}
          </p>
        </div>
        <span
          data-testid="ph30-eligible-badge"
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200"
        >
          {detail.pws_partner_status === "active"
            ? "Active partner" : detail.pws_partner_status}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            data-testid="ph30-enabled-toggle"
            checked={draft.enabled}
            onChange={(e) => update("enabled", e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-slate-300 text-cyan-600"
          />
          <div>
            <div className="text-sm font-semibold text-slate-800">
              Optional local sample collection enabled
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              When off, the booking page does not display any
              collection wording for this partner.
            </div>
          </div>
        </label>

        <div>
          <label className="text-[11px] font-bold uppercase text-slate-500">
            Price mode
          </label>
          <select
            data-testid="ph30-price-mode-select"
            value={draft.price_mode}
            onChange={(e) => update("price_mode", e.target.value)}
            className="mt-1 block w-full rounded-md border-slate-300 text-sm"
          >
            {Object.entries(PRICE_MODE_LABEL).map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </select>
        </div>

        {draft.price_mode === "admin_configured_price" ? (
          <div>
            <label className="text-[11px] font-bold uppercase text-slate-500">
              Configured price (AUD)
            </label>
            <input
              type="number"
              min={0}
              step="0.01"
              data-testid="ph30-price-input"
              value={draft.price}
              onChange={(e) => update("price", e.target.value)}
              className="mt-1 block w-full rounded-md border-slate-300 text-sm"
            />
          </div>
        ) : null}

        <div>
          <label className="text-[11px] font-bold uppercase text-slate-500">
            Contact method
          </label>
          <select
            data-testid="ph30-contact-method-select"
            value={draft.contact_method}
            onChange={(e) => update("contact_method", e.target.value)}
            className="mt-1 block w-full rounded-md border-slate-300 text-sm"
          >
            {Object.entries(CONTACT_METHOD_LABEL).map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </select>
        </div>

        <div className="sm:col-span-2">
          <label className="text-[11px] font-bold uppercase text-slate-500">
            Service area (free text — admin reference only)
          </label>
          <input
            type="text"
            maxLength={240}
            placeholder="e.g. Noosa Shire — Tewantin / Cooroy / Noosaville"
            data-testid="ph30-service-area-input"
            value={draft.service_area}
            onChange={(e) => update("service_area", e.target.value)}
            className="mt-1 block w-full rounded-md border-slate-300 text-sm"
          />
          <div className="text-[11px] text-slate-500 mt-1">
            Free-text reference only. Postcodes drive the
            customer-facing availability check.
          </div>
        </div>

        <div className="sm:col-span-2">
          <label className="text-[11px] font-bold uppercase text-slate-500">
            Configured postcodes (comma-separated)
          </label>
          <input
            type="text"
            placeholder="4566, 4567, 4568"
            data-testid="ph30-postcodes-input"
            value={draft.postcodes}
            onChange={(e) => update("postcodes", e.target.value)}
            className="mt-1 block w-full rounded-md border-slate-300 text-sm"
          />
          <div className="text-[11px] text-slate-500 mt-1">
            Booking page only shows collection wording when the
            customer&apos;s postcode matches.
          </div>
        </div>

        <div className="sm:col-span-2">
          <label className="text-[11px] font-bold uppercase text-slate-500">
            Note / instructions (max 1000 chars)
          </label>
          <textarea
            rows={3}
            maxLength={1000}
            placeholder="Use calm operational wording — collection may be available, confirmed before service."
            data-testid="ph30-note-input"
            value={draft.note}
            onChange={(e) => update("note", e.target.value)}
            className="mt-1 block w-full rounded-md border-slate-300 text-sm"
          />
          <div className="text-[11px] text-slate-500 mt-1">
            Banned: {`"guaranteed", "free collection", "urgent", "must collect", "technician has been dispatched", "job created"`}.
            Use {`"may be available"`} + {`"confirmed before service"`}.
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-slate-200">
        <button
          data-testid="ph30-save-btn"
          onClick={onSave}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving…" : "Save settings"}
        </button>
        {detail.settings.local_sample_collection_updated_at ? (
          <div
            data-testid="ph30-last-updated"
            className="text-xs text-slate-500"
          >
            Last updated{" "}
            {formatAU(detail.settings.local_sample_collection_updated_at)}
            {detail.settings.local_sample_collection_updated_by
              ? ` · by ${detail.settings.local_sample_collection_updated_by}`
              : ""}
          </div>
        ) : (
          <div className="text-xs text-slate-400">
            No changes have been saved yet.
          </div>
        )}
      </div>

      {detail.settings.local_sample_collection_enabled ? (
        <Preview detail={detail} />
      ) : null}
    </div>
  );
}


function Preview({ detail }) {
  const region = detail.franchise_region || "your local area";
  return (
    <div
      data-testid="ph30-preview"
      className="rounded-xl bg-slate-50 border border-slate-200 p-4 space-y-1.5"
    >
      <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">
        Customer-facing wording preview
      </div>
      <p className="text-sm text-slate-700 leading-relaxed">
        Optional local sample collection may be available through
        Pristine Water Systems {region}. Collection availability and
        any related cost will be confirmed before service. No
        collection is scheduled without your approval.
      </p>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500 mt-2">
        <span className="inline-flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          Postcodes: {(detail.settings.local_sample_collection_postcodes || []).join(", ") || "all"}
        </span>
        <span className="inline-flex items-center gap-1">
          <ListChecks className="w-3 h-3" />
          {PRICE_MODE_LABEL[detail.settings.local_sample_collection_price_mode]
            || "—"}
        </span>
        {detail.settings.local_sample_collection_contact_method
            === "partner_direct" ? (
          <span className="inline-flex items-center gap-1">
            <Phone className="w-3 h-3" /> Partner direct
          </span>
        ) : null}
        {detail.settings.local_sample_collection_contact_method
            === "scwt_admin" ? (
          <span className="inline-flex items-center gap-1">
            <Mail className="w-3 h-3" /> Aware admin
          </span>
        ) : null}
      </div>
    </div>
  );
}


function AuditPanel({ rows }) {
  return (
    <div
      data-testid="ph30-audit-panel"
      className="rounded-2xl bg-white border border-slate-200 p-6 space-y-3"
    >
      <div className="flex items-center gap-2">
        <History className="w-4 h-4 text-slate-500" />
        <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">
          Audit history
        </h3>
        <span className="text-xs text-slate-400">
          (append-only — last {rows.length})
        </span>
      </div>
      <ul className="divide-y divide-slate-100">
        {rows.map((row, i) => (
          <li
            key={row.id || i}
            data-testid={`ph30-audit-row-${i}`}
            className="py-2.5 text-xs text-slate-700"
          >
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-slate-500">
                {formatAU(row.created_at)}
              </span>
              <span className="text-slate-400">·</span>
              <span className="text-slate-600">
                {row.actor_email || "system"}
              </span>
            </div>
            <div className="mt-1 text-slate-600">
              Changed: {(row.changes || []).join(", ")}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
